import ButtonComponent from './button-component.js'
import { customElement, property } from 'lit/decorators.js'
import { html, css, CSSResultGroup, TemplateResult, svg } from 'lit'

// prettier-ignore
interface ComponentState {
  variant: string
  name: string
}

export const STATES: { [key: string]: ComponentState } = {
  DEFAULT: { variant: 'il-button--secondary', name: '' },
  SELECTED: { variant: 'il-button--primary', name: 'selected' },
  HAS_EXISTING_BOOKING: { variant: 'il-square-button--has-existing-booking', name: 'has-existing-booking' },
  UNAVAILABLE: { variant: 'il-button--tertiary', name: 'unavailable' },
}

export const PRICE_GROUPS = {
  LOW: 'low',
  MID: 'mid',
}

@customElement('il-square-button')
export default class SquareButtonComponent extends ButtonComponent {
  @property({ type: String })
  details: string = ''

  @property({ type: Boolean })
  selected: boolean = false

  @property({ type: Boolean })
  unavailable: boolean = false

  @property({ type: Boolean })
  unselectable: boolean = false

  @property({ attribute: 'price-group', type: String })
  priceGroup: string = ''

  @property({ attribute: 'grey-text', type: Boolean })
  greyText: boolean = false

  @property({ attribute: 'horizontal', type: Boolean })
  horizontal: boolean = false

  @property({ attribute: 'full-text', type: Boolean })
  fullText: boolean = false

  @property({ attribute: 'has-existing-booking', type: Boolean })
  hasExistingBooking: boolean = false

  @property({ type: String, attribute: 'tooltip-text' })
  tooltipText?: string

  @property({ type: Boolean, attribute: 'show-corner-flag' })
  showCornerFlag: boolean = false

  @property({ type: Boolean, attribute: 'show-dot-indicator'})
  showDotIndicator: boolean = false

  @property({ type: Boolean, attribute: 'ellipsis' })
  ellipsis: boolean = false

  @property({ type: Boolean, reflect: true, attribute: 'skeleton-loading' })
  skeletonLoading: boolean = false

  get showTooltip(): boolean {
    return Boolean(this.tooltipText) && (this.unavailable || this.showCornerFlag)
  }

  // prettier-ignore
  static override styles = [
    ButtonComponent.styles,
    css`
      .il-square-button {
        --il-sqr-btn-radius: 6px;

        border-width: var(--il-square-button-border-width, 1px);
        border-radius: var(--il-sqr-btn-radius);
        height: var(--il-size-48);
        padding: 0.375rem;

        position: relative;
      }

      .il-square-button .il-square-button__detail {
        font-size: var(--il-font-size-12);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
      }

      .il-square-button.il-button--secondary .il-square-button__detail,
      .il-square-button.il-button--secondary:hover .il-square-button__detail,
      .il-square-button.il-button--secondary:active .il-square-button__detail,
      .il-square-button.il-button--secondary:focus-visible .il-square-button__detail {
        color: var(--il-color-neutrals-300);
      }

      .il-square-button.il-button--secondary {
        border-color: var(--il-color-neutrals-200);
      }

      .il-button.il-square-button.il-button--secondary:hover {
        border-color: var(--il-color-neutrals-250);
      }

      .il-button.il-square-button.il-square-button--grey-text,
      .il-button.il-square-button.il-square-button--grey-text:focus-visible {
        color: var(--il-color-neutrals-300);
      }

      .il-button.il-square-button.il-square-button--grey-text:hover {
        color: var(--il-color-neutrals-350);
      }

      .il-square-button.il-square-button--grey-text.il-button--primary {
        color: var(--il-color-neutrals-0);
        background-color: var(--il-color-accent-300);
      }

      .il-square-button.il-square-button--grey-text.il-button--primary:hover {
        color: var(--il-color-neutrals-0);
        background-color: var(--il-color-accent-400);
      }

      .il-square-button.il-square-button--grey-text.il-button--primary:focus {
        color: var(--il-color-neutrals-0);
        border-color: var(--il-color-accent-200);
      }

      .il-square-button.il-button--tertiary,
      .il-square-button.il-button--tertiary:hover,
      .il-square-button.il-button--tertiary:active,
      .il-square-button.il-button--tertiary:focus-visible {
        border-color: var(--il-color-neutrals-350);
      }

      .il-button.il-square-button:not(.il-button--primary).il-square-button--low-price,
      .il-button.il-square-button:not(.il-button--primary).il-square-button--mid-price {
        color: var(--il-color-neutrals-300);
      }

      .il-button.il-square-button:not(.il-button--primary).il-square-button--low-price:hover,
      .il-button.il-square-button:not(.il-button--primary).il-square-button--low-price:active,
      .il-button.il-square-button:not(.il-button--primary).il-square-button--low-price:focus-visible,
      .il-button.il-square-button:not(.il-button--primary).il-square-button--mid-price:hover,
      .il-button.il-square-button:not(.il-button--primary).il-square-button--mid-price:active,
      .il-button.il-square-button:not(.il-button--primary).il-square-button--mid-price:focus-visible {
        color: var(--il-color-neutrals-350);
      }

      .il-button.il-square-button:not(.il-button--primary).il-square-button--low-price .il-square-button__detail {
        color: var(--il-color-success-300);
        font-weight: var(--il-font-weight-xbold);
      }

      .il-button.il-square-button:not(.il-button--primary).il-square-button--mid-price .il-square-button__detail {
        color: var(--il-color-neutrals-300);
        font-weight: var(--il-font-weight-bold);
      }

      .il-button.il-square-button.il-square-button--has-existing-booking,
      .il-button.il-square-button.il-square-button--has-existing-booking:hover,
      .il-button.il-square-button.il-square-button--has-existing-booking:active,
      .il-button.il-square-button.il-square-button--has-existing-booking:focus-visible {
        background-color: var(--il-color-neutrals-0);
        border-color: var(--il-color-neutrals-350);
        color: var(--il-color-neutrals-350);
        font-weight: var(--il-font-weight-xbold);
      }

      .il-button.il-square-button.il-square-button--has-existing-booking .il-square-button__details {
        color: var(--il-color-neutrals-300);
        font-weight: var(--il-font-weight-bold);
      }

      .il-square-button.il-square-button--horizontal .il-square-button__content {
        display: flex;
        align-items: center;
        height: var(--il-size-40);
      }

      .il-square-button.il-square-button--full-text {
        display: block;
        height: auto;
      }

      .il-square-button.il-square-button--full-text .il-square-button__content {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding-top: 8px;
        padding-bottom: 8px;
      }

      .il-square-button.il-square-button--full-text .il-square-button__label {
        flex-wrap: wrap;
      }

      .il-square-button.il-square-button--full-text [data-test-il-square-button-tooltip] {
        text-align: left;
      }

      .il-square-button.il-square-button--full-text [data-test-il-square-button-tooltip]::part(tooltip-content) {
        align-items: flex-start;
      }

      .il-square-button.il-square-button--full-text .il-square-button__detail {
        flex-shrink: 0;
      }

      .il-square-button--small {
        height: var(--il-size-40);
      }

      .il-square-button.il-button.il-square-button--unavailable,
      .il-square-button.il-button.il-square-button--unavailable:hover,
      .il-square-button.il-button.il-square-button--unavailable:active {
        background: var(--il-color-neutrals-50);
        border-style: solid;
        border-color: var(--il-color-neutrals-100);
        color: var(--il-color-neutrals-250);
        opacity: 1;
      }

      .il-square-button.il-button.il-square-button--unselectable,
      .il-square-button.il-button.il-square-button--unselectable:hover,
      .il-square-button.il-button.il-square-button--unselectable:active {
        background: var(--il-color-neutrals-50);
        border-style: solid;
        border-color: var(--il-color-neutrals-100);
        color: var(--il-color-neutrals-300);
        opacity: 1;
        pointer-events: none;
      }

      .il-square-button__corner-flag{
        position: absolute;
        bottom: 0;
        right: 0;
        width: 16px;
        height: 16px;
        pointer-events: none;
        border-radius: 0 0 calc(var(--il-sqr-btn-radius) - 1px) 0;
        overflow: hidden;
      }

      .il-square-button.il-square-button--dot-indicator::after {
        content: '';
        position: absolute;
        top: 0.375rem;
        right: 0.375rem;
        width: 6px;
        height: 6px;
        background-color: var(--il-color-accent-200);
        border-radius: 50%;
        pointer-events: none;
      }

      .il-square-button.il-square-button--ellipsis .il-square-button__label {
        max-width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      .il-square-button .il-square-button__horizontal-divisor {
        content: '•';
        color: var(--il-color-neutrals-200);
        font-size: var(--il-font-size-12);
        padding: 0 6px;
      }

      [data-test-il-square-button-tooltip],
      [data-test-il-square-button-tooltip]::part(tooltip-trigger),
      [data-test-il-square-button-tooltip]::part(tooltip-content) {
        width: 100%;
        height: 100%;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      /* Prevent the tooltip popup text from inheriting the primary variant's white color */
      .il-button--primary [data-test-il-square-button-tooltip] {
        --il-tooltip-text-color: var(--il-color-neutrals-300);
      }

      .il-square-button.il-square-button--horizontal [data-test-il-square-button-tooltip]::part(tooltip-trigger) {
        flex-direction: row;
      }

      /* skeleton loader styles */
      .il-square-button.il-square-button--skeleton {
        cursor: default;
        pointer-events: none;
      }

      .il-square-button.il-square-button--skeleton::before {
        content: '';
        position: absolute;
        inset: 0;
        animation: skeleton-shimmer 1.5s ease-in-out infinite;
        background: linear-gradient(
          90deg,
          var(--il-color-neutrals-100) 25%,
          var(--il-color-neutrals-50) 50%,
          var(--il-color-neutrals-100) 75%
        );
        background-size: 200% 100%;
        border-radius: var(--il-sqr-btn-radius);
      }
    `,
  ] as CSSResultGroup

  override render() {
    const priceClass = this.priceGroup ? `il-square-button--${this.priceGroup}-price` : ''

    return html`
      <button
        class="
          ${this.baseCSSClass}
          il-button
          il-square-button
          ${this.getState().variant}
          ${this.greyText ? `il-square-button--grey-text` : ''}
          ${this.horizontal ? 'il-square-button--horizontal' : ''}
          ${this.fullText ? 'il-square-button--full-text' : ''}
          ${this.disabled ? 'il-button--disabled' : ''}
          ${this.unavailable ? 'il-square-button--unavailable' : ''}
          ${this.unselectable ? 'il-square-button--unselectable' : ''}
          ${this.fill ? 'il-button--fill-container' : ''}
          ${this.opaque ? 'il-button--opaque' : ''}
          ${this.small ? 'il-square-button--small' : ''}
          ${this.showDotIndicator ? 'il-square-button--dot-indicator' : ''}
          ${this.ellipsis ? 'il-square-button--ellipsis' : ''}
          ${this.skeletonLoading ? 'il-square-button--skeleton' : ''}
          ${priceClass}
        "
        part="button"
        ?disabled=${this.unavailable || this.disabled || this.skeletonLoading}
        @click=${() => this._onClick()}
        data-test-iliad-square-button=${this.getState().name}
      >
        <il-tooltip
          data-test-il-square-button-tooltip
          .disableContentHovering=${!this.showTooltip}
          tooltip-text="${this.tooltipText!}"
          tooltip-position="top"
          tooltip-variant="default"
        >
          <div class="il-square-button__content">
            <div class="il-square-button__label" data-test-iliad-square-button-label>
              <slot name="pre-label"></slot>
              <slot name="label">${this.label}</slot>
              <slot name="post-label"></slot>
            </div>

            ${this.renderHorizontalDivisor()}

            <div class="il-square-button__detail" data-test-iliad-square-button-detail>
              <slot name="details">
                ${this.details}
              </slot>
            </div>
          </div>
        </il-tooltip>

        ${this.renderCornerFlag()}
      </button>
    `
  }

  private renderCornerFlag(): TemplateResult {
    if (this.showCornerFlag) {
      return svg`
        <svg data-testid="iliad-square-button__corner-flag" class="il-square-button__corner-flag" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_15264_81471)">
            <path d="M16 0L16 16L-6.99382e-07 16L16 0Z" fill="var(--il-color-accent-400)"/>
            <line x1="16.3536" y1="0.353553" x2="0.353553" y2="16.3536" stroke="white"/>
          </g>
          <defs>
            <clipPath id="clip0_15264_81471">
            <rect width="16" height="16" fill="white"/>
            </clipPath>
          </defs>
        </svg>
      `
    }

    return html``
  }

  private renderHorizontalDivisor(): TemplateResult {
    if (this.horizontal && !this.fullText) {
      return html`<div class="il-square-button__horizontal-divisor">•</div>`
    }

    return html``
  }

  private getState(): ComponentState {
    if (this.selected) {
      return STATES.SELECTED
    }

    if (this.unavailable) {
      return STATES.UNAVAILABLE
    }

    if (this.hasExistingBooking) {
      return STATES.HAS_EXISTING_BOOKING
    }

    return STATES.DEFAULT
  }
}
